import { useCallback } from "react";

export default function PositionPermissionModalActions({
  normalizePermissionKeys,
  setPositionPermissionSelection,
  positionEditDraft,
  positionDraft,
  setPositionPermissionModal,
  setPositionPermissionSearch,
  groupedPermissionRows,
  setExpandedPermissionGroupByTarget,
  positionPermissionModal,
  permissionItemRefs,
  positionPermissionSelection,
  setPositionEditDraft,
  setPositionDraft
}) {
  const togglePermissionSelection = useCallback((permissionKey, checked) => {
    const key = String(permissionKey || "").trim();
    if (!key) {
      return;
    }

    setPositionPermissionSelection((prev) => {
      const next = new Set(normalizePermissionKeys(prev));
      if (checked) {
        next.add(key);
      } else {
        next.delete(key);
      }
      return Array.from(next);
    });
  }, [normalizePermissionKeys, setPositionPermissionSelection]);

  const openPositionPermissionModal = useCallback((target) => {
    const mode = String(target || "") === "create" ? "create" : "edit";
    const source = mode === "edit" ? positionEditDraft.permissions : positionDraft.permissions;
    setPositionPermissionSelection(normalizePermissionKeys(source));
    setPositionPermissionModal({ mode, target });
    setPositionPermissionSearch("");
    const firstGroup = groupedPermissionRows[0]?.group || "";
    if (firstGroup) {
      setExpandedPermissionGroupByTarget((prev) => ({ ...prev, [target]: firstGroup }));
    }
  }, [
    groupedPermissionRows,
    normalizePermissionKeys,
    positionDraft.permissions,
    positionEditDraft.permissions,
    setExpandedPermissionGroupByTarget,
    setPositionPermissionModal,
    setPositionPermissionSearch,
    setPositionPermissionSelection
  ]);

  const closePositionPermissionModal = useCallback(() => {
    setPositionPermissionModal(null);
    setPositionPermissionSelection([]);
    setPositionPermissionSearch("");
  }, [setPositionPermissionModal, setPositionPermissionSearch, setPositionPermissionSelection]);

  const focusPermissionSelection = useCallback((permissionKey) => {
    const key = String(permissionKey || "").trim();
    if (!key) {
      return;
    }

    const target = String(positionPermissionModal?.target || "");
    const group = key.split(":")[0] || "other";
    setExpandedPermissionGroupByTarget((prev) => ({ ...prev, [target]: group }));

    if (typeof window !== "undefined") {
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          const item = permissionItemRefs.current[key];
          if (item && typeof item.scrollIntoView === "function") {
            item.scrollIntoView({ behavior: "smooth", block: "center" });
            const checkbox = item.querySelector("input[type='checkbox']");
            if (checkbox && typeof checkbox.focus === "function") {
              checkbox.focus({ preventScroll: true });
            }
          }
        });
      });
    }
  }, [permissionItemRefs, positionPermissionModal?.target, setExpandedPermissionGroupByTarget]);

  const quickSelectPermission = useCallback((permissionKey) => {
    const key = String(permissionKey || "").trim();
    if (!key) {
      return;
    }

    togglePermissionSelection(key, true);
    focusPermissionSelection(key);
  }, [focusPermissionSelection, togglePermissionSelection]);

  const togglePermissionGroup = useCallback((target, group) => {
    setExpandedPermissionGroupByTarget((prev) => ({
      ...prev,
      [target]: prev[target] === group ? "" : group
    }));
  }, [setExpandedPermissionGroupByTarget]);

  const applyPositionPermissionSelection = useCallback(() => {
    const next = normalizePermissionKeys(positionPermissionSelection);
    if (positionPermissionModal?.mode === "edit") {
      setPositionEditDraft((prev) => ({ ...prev, permissions: next }));
    } else {
      setPositionDraft((prev) => ({ ...prev, permissions: next }));
    }
    closePositionPermissionModal();
  }, [
    closePositionPermissionModal,
    normalizePermissionKeys,
    positionPermissionModal?.mode,
    positionPermissionSelection,
    setPositionEditDraft,
    setPositionDraft
  ]);

  return {
    togglePermissionSelection,
    openPositionPermissionModal,
    closePositionPermissionModal,
    quickSelectPermission,
    focusPermissionSelection,
    togglePermissionGroup,
    applyPositionPermissionSelection
  };
}
